import { Fragment, useState, useEffect, MouseEvent } from "react"
import { observer } from "mobx-react"
import { Dialog, Disclosure, Transition } from "@headlessui/react"
import classNames from "classnames"
import { useStore } from "@features/common"
import { IconChevronUp } from "@lib/icons"
import { useScrollable } from "@lib/scroll"

interface TableOfContentsProps {
  tableOfContents: {
    id: string
    slug: string
    title: string
  }[]
}

export const DesktopTableOfContents = observer(function FaqTableOfContents({
  tableOfContents,
}: TableOfContentsProps) {
  const {
    GlobalModel: { getTemplate },
  } = useStore()
  const {
    isEndOfScrollingDown,
    isScrollable,
    onScroll,
    onRefChange,
  } = useScrollable()

  function clickHandler(element_id: string, element_slug?: string) {
    return function scrollToElement(e: MouseEvent<HTMLAnchorElement>) {
      if (!element_slug) {
        location.hash = ""
        e.preventDefault()
      }
      const yOffset = DESKTOP_Y_OFFSET
      scrollTo(element_id, {
        yOffset,
        container: document.querySelector("main") ?? document.body,
      })
    }
  }

  return (
    <Disclosure defaultOpen={true}>
      {({ open }) => (
        <Fragment>
          <Disclosure.Button
            className="space-x-5 p-8 w-full flex items-center justify-between font-sans text-3xl text-black font-normal tracking-2 uppercase border border-gray-100 focus:ring-0"
            data-test-id="faq__contents"
          >
            <span>{getTemplate("table.of.contents")}</span>
            <IconChevronUp
              className={classNames("flex-shrink-0", {
                "transform rotate-180": !open,
              })}
            />
          </Disclosure.Button>
          <Transition
            as={Fragment}
            show={open}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Disclosure.Panel>
              <div className="relative">
                <ul
                  ref={onRefChange}
                  onScroll={onScroll}
                  className={classNames(
                    "relative space-y-10 !m-0 !p-8 overflow-y-auto h-[calc(100vh-250px)] flex flex-col",
                    "bg-white border-l border-r border-b border-gray-100 list-none scrollbar-width-none",
                  )}
                >
                  {tableOfContents
                    .filter((i) => !!(i.title && i.slug))
                    .map((item, index) => (
                      <li key={item.id} className="space-x-3 flex items-start">
                        <span className="relative w-7 h-7 flex flex-shrink-0 items-center justify-center bg-gray-100 rounded-full">
                          <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pl-px h-4 text-lg leading-none tracking-normal text-black">
                            {index + 1}
                          </span>
                        </span>
                        <a
                          onClick={clickHandler(item.id, item.slug)}
                          href={`#${item.slug || ""}`}
                          className="cursor-pointer inline-flex text-22 text-gray-300 text-left underline underline-thickness-1 underline-offset-6 hover:no-underline"
                          data-test-id="faq__contents_item"
                        >
                          {item.title}
                        </a>
                      </li>
                    ))}
                </ul>
                {isScrollable && !isEndOfScrollingDown && open && (
                  <ShadowBottom />
                )}
              </div>
            </Disclosure.Panel>
          </Transition>
        </Fragment>
      )}
    </Disclosure>
  )
})

export const TouchTableOfContents = observer(function FaqTableOfContents({
  tableOfContents,
}: TableOfContentsProps) {
  const {
    ResponsiveModel: { mobile },
    GlobalModel: { getTemplate },
  } = useStore()
  const [opened, setOpened] = useState(false)
  const {
    isEndOfScrollingDown,
    isScrollable,
    onScroll,
    onRefChange,
  } = useScrollable()

  useEffect(() => {
    const main = document.querySelector("main")
    if (opened) {
      if (main) main.style["overflow-y"] = "hidden"
    } else {
      if (main) main.style["overflow-y"] = "scroll"
    }
  }, [opened])

  function clickHandler(element_id: string, element_slug?: string) {
    return function scrollToElement(e: MouseEvent<HTMLAnchorElement>) {
      if (!element_slug) {
        location.hash = ""
        e.preventDefault()
      }
      const yOffset = mobile ? MOBILE_Y_OFFSET : TABLET_Y_OFFSET
      scrollTo(element_id, {
        yOffset,
        container: document.querySelector("main") ?? document.body,
      })
      setOpened(false)
    }
  }

  return (
    <Fragment>
      <button
        onClick={() => setOpened(true)}
        className="space-x-5 px-6 py-4 w-full flex items-center justify-between font-sans text-lg text-black font-normal tracking-2 uppercase bg-white border border-gray-100 focus:ring-0 tablet:text-2xl laptop:hidden"
      >
        <span>{getTemplate("table.of.contents")}</span>
        <IconChevronUp
          className={classNames("flex-shrink-0", {
            "transform rotate-180": !opened,
          })}
        />
      </button>
      <Transition
        as={Fragment}
        show={opened}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Dialog
          as="div"
          className="fixed inset-0 z-10 pt-[130px] px-4 tablet:pt-[9.875rem] tablet:px-10 laptop:hidden"
          open={opened}
          onClose={() => setOpened(false)}
        >
          <Dialog.Overlay className="fixed inset-0" />
          <div className="relative">
            <div
              ref={onRefChange}
              onScroll={onScroll}
              className={classNames(
                "relative space-y-6 px-6 py-8 overflow-y-scroll h-auto max-h-[65vh] flex flex-col bg-white border-l border-r border-b border-gray-100 list-none scrollbar-width-none bottom-fade",
                "bottom-shadow",
                "tablet:space-y-10 tablet:max-h-[calc(100vh-184px)]",
              )}
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {tableOfContents
                .filter((i) => !!(i.title && i.slug))
                .map((item, index) => (
                  <li key={item.id} className="space-x-3 flex items-start">
                    <span className="relative w-7 h-7 flex flex-shrink-0 bg-gray-100 rounded-full">
                      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pl-px h-4 text-lg leading-none tracking-normal text-black">
                        {index + 1}
                      </span>
                    </span>
                    <a
                      onClick={clickHandler(item.id, item.slug)}
                      href={`#${item.slug || ""}`}
                      className="cursor-pointer inline-flex text-22 text-gray-300 text-left underline underline-thickness-1 underline-offset-6 hover:no-underline"
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
            </div>
            {isScrollable && !isEndOfScrollingDown && opened && (
              <ShadowBottom />
            )}
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  )
})

function ShadowBottom() {
  return (
    <div
      className="absolute right-px bottom-px left-px h-16 pointer-events-none"
      style={{
        background:
          "linear-gradient(360deg, #ffffff 25%, rgba(255, 255, 255, 0) 100%)",
      }}
    />
  )
}

const DESKTOP_Y_OFFSET = 88
const TABLET_Y_OFFSET = 160
const MOBILE_Y_OFFSET = 152

interface ScrollOptions {
  yOffset?: number
  behavior?: ScrollBehavior
  container?: HTMLElement
}

function scrollTo(element_id: string, options: ScrollOptions = {}) {
  const { yOffset = 0, container = document.body, ...otherOptions } = options
  const element = document.getElementById(element_id)

  if (element) {
    const top =
      element.getBoundingClientRect().top + container.scrollTop - yOffset

    container.scrollTo({
      top,
      ...otherOptions,
    })
  }
}
